/**
 * tradingPhase.ts
 * Constants for TradingHub phase management and header session actions.
 *
 * Provides:
 * - Status ↔ Phase mapping constants
 * - Transition confirmation modal configs
 * - Per-status session dropdown menu actions
 *
 * @see types/shared/TradingPhase.ts — type definitions
 */

import type { SessionDraft } from '@/types/transient/SessionDraft';
import type {
  TradingPhase,
  HeaderTransitionKey,
  TransitionConfig,
  SessionMenuAction,
} from '@/types/shared/TradingPhase';

// ===== PHASE ↔ STATUS MAPPINGS =====

/** Maps SessionDraft.status to TradingHub UI phase */
export const STATUS_TO_PHASE: Readonly<Record<SessionDraft['status'], TradingPhase>> = {
  'pre-trading': 'pre-trading',
  'live': 'live-trading',
  'post-trading': 'post-trading',
  'review': 'post-trading',
  'shelved': 'post-trading',
} as const;

/** Maps TradingHub UI phase back to the primary SessionDraft.status */
export const PHASE_TO_STATUS: Readonly<Record<TradingPhase, SessionDraft['status']>> = {
  'pre-trading': 'pre-trading',
  'live-trading': 'live',
  'post-trading': 'post-trading',
} as const;

// ===== HEADER TRANSITION CONFIGS =====

/**
 * Confirmation modal configs for phase transitions triggered from the header
 * Session dropdown. Keyed by HeaderTransitionKey for compile-time safety.
 */
export const HEADER_TRANSITION_CONFIGS: Readonly<Record<HeaderTransitionKey, TransitionConfig>> = {
  'return-to-pre-trading': {
    key: 'return-to-pre-trading',
    title: 'Return to Pre-Trading?',
    description: 'You will return to the pre-trading preparation phase. The session timer will keep running.',
    confirmLabel: 'Return to Pre-Trading',
    confirmColor: 'primary',
    targetPhase: 'pre-trading',
  },
  'end-trading-session': {
    key: 'end-trading-session',
    title: 'End Trading Session?',
    description: 'This will close your active trading session and move to the post-trading review phase. You will not be able to return to live trading.',
    confirmLabel: 'End Session',
    confirmColor: 'warning',
    targetPhase: 'post-trading',
  },
} as const;

// ===== SESSION MENU ACTIONS =====

/**
 * Per-status session dropdown menu actions.
 *
 * Requirements (T07):
 * - pre-trading: Save & Exit only (inline "Start Live Trading" button is the sole start-live CTA)
 * - live: Return to Pre-Trading, End Trading Session, Save & Exit
 * - post-trading: Save & Exit only (post-trading → review is S03 scope)
 * - review: Save & Exit only (terminal read-only state)
 * - shelved: Save & Exit only (defensive — shelve is not its own phase)
 */
export const SESSION_MENU_ACTIONS: Readonly<Record<SessionDraft['status'], readonly SessionMenuAction[]>> = {
  'pre-trading': [
    { key: 'save-exit', label: 'Save & Exit', type: 'save-exit' },
  ],
  'live': [
    { key: 'return-to-pre-trading', label: 'Return to Pre-Trading', type: 'transition', transitionConfig: HEADER_TRANSITION_CONFIGS['return-to-pre-trading'] },
    { key: 'end-trading-session', label: 'End Trading Session', type: 'transition', transitionConfig: HEADER_TRANSITION_CONFIGS['end-trading-session'] },
    { key: 'save-exit', label: 'Save & Exit', type: 'save-exit' },
  ],
  'post-trading': [
    { key: 'save-exit', label: 'Save & Exit', type: 'save-exit' },
  ],
  'review': [
    { key: 'save-exit', label: 'Save & Exit', type: 'save-exit' },
  ],
  'shelved': [
    { key: 'save-exit', label: 'Save & Exit', type: 'save-exit' },
  ],
} as const;
